import { useEffect, useState } from "react";
import { LoginForm } from "./LogInForm";
import { RegistrationForm } from "./RegistrationForm";
import { Box, Button, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";
import { login, register, sendOtp, verifyOtp } from "../redux/authSlice";
import { registerDevice, getUserInfo } from "../redux/userSlice";
import { initializeCrypto } from "../services/crypto";
import { fetchContacts } from "../redux/contactSlice";
import { useFirebaseMessaging } from "../hooks/useFirebaseMessaging";
import {
  setAuthMode,
  setCurrentNavigationView,
  setIsCompletingLogin,
  setSelectedChatView,
  setSelectedWalletTab,
} from "../redux/uiSlice";
import { AuthMode } from "../types/authMode";
import OTPVerification from "./Validation/OTPInput";
import { PhoneLoginForm } from "./PhoneLoginForm";
import { fetchMessageContacts } from "../redux/chatListSlice";
import SmartContractService from "../services/smartContractService";
import { combineAndDecryptEVMWalletPrivateKey } from "../utils/security";
import { fetchPolBalance } from "../redux/ndeipiCoinSlice";

export const AuthForm = () => {
  const [formData, setFormData] = useState({
    userName: "",
    password: "",
    emailAddress: "",
    name: "",
    surname: "",
    phoneNumber: "",
    // ssn: "",
    // dateOfBirth: null as Date | null,
    confirmPassword: "",
    isCharity: false,
  });

  const { loading: isAuthenticating } = useAppSelector((state) => state.auth);
  const [error, setError] = useState("");
  const dispatch = useAppDispatch();
  const { requestNotificationPermission } = useFirebaseMessaging();
  const { chainType } = useAppSelector((state) => state.wallet);
  const { authMode } = useAppSelector((state) => state.ui);
  const [phoneNumber, setPhoneNumber] = useState("");
  const { userExists } = useAppSelector((state) => state.user);
  const { messagingEnabled } = useAppSelector((state) => state.ui);

  const completeLogin = async () => {
    dispatch(setIsCompletingLogin(true));
    try {
      await initializeCrypto();
      const fcmToken = await requestNotificationPermission();
      const result = await dispatch(getUserInfo());

      if (getUserInfo.fulfilled.match(result)) {
        if (!result.payload?.walletAddress) {
          dispatch(setSelectedWalletTab("create"));
          dispatch(setCurrentNavigationView("wallet"));
        }

        //fetch POL balance
        await dispatch(fetchPolBalance());

        if (chainType !== "evm") {
          console.log("Skipping wallet connection for non-EVM chain");
          return;
        }

        if (result.payload?.walletAddress && !result.payload?.securityTokenA) {
          SmartContractService.connectWallet().then(async () => {
            await SmartContractService.initializeContracts();
          });
        }

        if (result.payload?.walletAddress && result.payload?.securityTokenA) {
          let pk = "";
          if (chainType === "evm") {
            pk = combineAndDecryptEVMWalletPrivateKey([
              result.payload?.securityTokenA ?? "",
              result.payload?.verificationCodeB ?? "",
              result.payload?.accessPhraseC ?? "",
              result.payload?.identityMarkerD ?? "",
            ]);
          }

          await SmartContractService.initializeContractsInternal(
            pk,
            result.payload?.walletAddress
          );
        }

        //
      }

      // const pk = combineAndDecryptWalletPrivateKey([
      //   user?.securityTokenA ?? "",
      //   user?.verificationCodeB ?? "",
      //   user?.accessPhraseC ?? "",
      //   user?.identityMarkerD ?? "",
      // ]);

      await dispatch(fetchContacts());
      if (messagingEnabled) {
        await dispatch(fetchMessageContacts());
        await dispatch(setSelectedChatView("list"));
      }

      if (fcmToken) {
        await dispatch(
          registerDevice({ fcmToken: fcmToken, deviceType: "web" })
        );
      }
    } catch (err) {
      setError(`Login failed: ${err}`);
    } finally {
      dispatch(setIsCompletingLogin(false));
    }
  };

  const handleLogin = async () => {
    try {
      // if (!userExists) {
      //   setError("Error Logging in");
      //   return;
      // }

      await dispatch(
        login({
          userNameOrEmailAddress: formData.userName,
          password: formData.password,
          rememberClient: false,
        })
      );
      if (!messagingEnabled) {
        await dispatch(setCurrentNavigationView("home"));
      }
      await completeLogin();
    } catch (err) {
      setError(`Login failed: ${err}`);
    }
  };

  const handlePhoneLogin = async () => {
    try {
      await dispatch(sendOtp({ phoneNumber }));
      dispatch(setAuthMode("otp" as AuthMode));
    } catch (err) {
      setError(`Failed to send OTP: ${err}`);
    }
  };

  const handleOtpVerification = async (otp: string) => {
    try {
      await dispatch(verifyOtp({ phoneNumber, otp }));
      await completeLogin();
    } catch (err) {
      setError(`OTP verification failed: ${err}`);
    }
  };

  const handleResendOtp = async () => {
    try {
      await dispatch(sendOtp({ phoneNumber }));
    } catch (err) {
      setError(`Failed to resend OTP: ${err}`);
    }
  };

  const handleRegister = async () => {
    const updatedFormData = {
      ...formData,
      userName: formData.emailAddress,
      password: formData.password,
      shopId: chainType === "evm" ? 5 : 4,
      isCharity: formData.isCharity,
    };

    const result = await dispatch(register(updatedFormData));

    if (register.fulfilled.match(result)) {
      setFormData(updatedFormData);
    } else if (register.rejected.match(result)) {
      const errorPayload = result.payload as any;

      if (errorPayload?.message) {
        setError(errorPayload.message);
      } else {
        // Fallback for unexpected error formats
        setError("Registration failed. Please try again.");
      }
    }
  };

  const handleGoogleSignUp = () => {
    console.log("Signing up with Google");
    // Implement Google OAuth
  };

  const handleFacebookSignUp = () => {
    console.log("Signing up with Facebook");
    // Implement Facebook OAuth
  };

  useEffect(() => {
    if (formData.userName === formData.emailAddress && formData.userName) {
      handleLogin();
    }
  }, [formData.userName]);

  return (
    <Box sx={{ maxWidth: 400, mx: "auto", p: 3 }}>
      <Typography variant="h5" align="center" gutterBottom>
        {authMode === "login" && "Login"}
        {authMode === "register" && "Create Account"}
        {authMode === "phone" && "Mobile Login"}
        {authMode === "otp" && "Verify OTP"}
      </Typography>

      {authMode === "login" ? (
        <LoginForm
          username={formData.userName}
          password={formData.password}
          isAuthenticating={isAuthenticating}
          error={error}
          onUsernameChange={(value) =>
            setFormData({ ...formData, userName: value })
          }
          onPasswordChange={(value) =>
            setFormData({ ...formData, password: value })
          }
          onSubmit={handleLogin}
          afterExternalLogin={completeLogin}
          onSwitchToRegister={() => dispatch(setAuthMode("register"))}
          onSwitchToPhoneLogin={() => dispatch(setAuthMode("phone"))}
        />
      ) : authMode === "register" ? (
        <RegistrationForm
          username={formData.userName}
          email={formData.emailAddress}
          firstName={formData.name}
          lastName={formData.surname}
          phone={formData.phoneNumber}
          // ssn={formData.ssn}
          // dateOfBirth={formData.dateOfBirth}
          password={formData.password}
          confirmPassword={formData.confirmPassword}
          error={error}
          onUsernameChange={(value) =>
            setFormData({ ...formData, userName: value })
          }
          onEmailChange={(value) =>
            setFormData({ ...formData, emailAddress: value })
          }
          onFirstNameChange={(value) =>
            setFormData({ ...formData, name: value })
          }
          onLastNameChange={(value) =>
            setFormData({ ...formData, surname: value })
          }
          onPhoneChange={(value) =>
            setFormData({ ...formData, phoneNumber: value })
          }
          // onSsnChange={(value) => {
          //   console.log("SSN value received in AuthForm:", value);
          //   setFormData({ ...formData, ssn: value });
          // }}
          // onDateOfBirthChange={(value) =>
          //   setFormData({ ...formData, dateOfBirth: value })
          // }
          onPasswordChange={(value) =>
            setFormData({ ...formData, password: value })
          }
          onConfirmPasswordChange={(value) =>
            setFormData({ ...formData, confirmPassword: value })
          }
          onBackToLogin={() => dispatch(setAuthMode("login"))}
          onSubmit={handleRegister}
          isSubmitting={isAuthenticating}
          onGoogleSignUp={handleGoogleSignUp}
          onFacebookSignUp={handleFacebookSignUp}
        />
      ) : authMode === "phone" ? (
        <PhoneLoginForm
          phoneNumber={phoneNumber}
          onPhoneNumberChange={setPhoneNumber}
          onSubmit={handlePhoneLogin}
          onBackToLogin={() => dispatch(setAuthMode("login"))}
          isSubmitting={isAuthenticating}
          error={error}
        />
      ) : (
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <OTPVerification
            onComplete={handleOtpVerification}
            error={error}
            resendOtp={handleResendOtp}
          />

          <Button
            variant="outlined"
            onClick={() => dispatch(setAuthMode("login"))}
            sx={{ mt: 2 }}
          >
            Use Password Login
          </Button>
        </Box>
      )}
    </Box>
  );
};
